import * as vars from './vars.mjs';
import * as util from '../util.mjs';
import * as displaySupport from './display-support.mjs';

/** 
 * Logic that supports the processing of user clicks on post pages. These scripts will want to be 
 * loaded after the DOM. These should be ranked high in terms of load priority, as the user could 
 * click on something in the UI as soon as it is displayed. 
 */

/**
 * Handle the user clicking the comment count in the post byline. This primarily involves 
 * activating the comment pane (if necessary) and bringing it into view.
 */
export const handleClickCommentCount = function(event) {
	//console.log('event = %o', event);
	/*
	 * Prevent the context click event from performing the default action, as we are overriding 
	 * that behavior.
	 */
	event.preventDefault();

	displaySupport.activateAndScrollToCommentsPane(vars.commentsBoxEle);
};

/**
 * Handle the user clicking the "reply" widget that accompanies each comment. This primarily 
 * involves displaying the comment input form adjacent to the "reply" widget that was clicked.
 */
export const handleClickCommentReply = function(event) {
	//console.log('event = %o', event);
	/*
	 * Prevent the context click event from performing the default action, as we are overriding 
	 * that behavior.
	 */
	event.preventDefault();

	cleanUpLeftoverCommentReplyCancelLink();
	placeCommentFormInReadyState();
	moveCommentFormForReply(event.target);

	// Place focus in the comment textarea element.
	const commentTextareaEle = vars.commentFormEle.querySelector('textarea[name="fields[comment]"]');
	if (commentTextareaEle !== null) {
		commentTextareaEle.focus();
	}

	identifyParentCommentInCommentForm(event.target);
	displayCancelReplyWidget(event.target);
};

/**
 * Handle the user clicking the "cancel reply" widget that accompanies each comment. This 
 * primarily involves undoing the display of the comment input form adjacent to the 
 * "cancel reply" widget that was clicked.
 */
export const handleClickCommentReplyCancel = function(event) {
	//console.log('event = %o', event);
	/*
	 * Prevent the context click event from performing the default action, as we are overriding 
	 * that behavior.
	 */
	event.preventDefault();

	/*
	 * Wherever on the page the comment form is, insert it in the DOM above any existing comments.
	 */
	const commentReplyCancelLinkEle = event.target;
	vars.commentPaneEle.insertBefore(
		vars.commentFormBoxEle, vars.commentPaneEle.firstChild);
	vars.commentFormBoxEle.classList.remove('comment-form-reply-box');

	identifyParentAsPostInCommentForm();
	displayReplyWidget(event.target);
};

/**
 * Make preparations to handle interaction with the after-post panes.
 */
export const prepareForAfterPostPaneInteraction = function() {
	addEventListenersToAfterPostPaneTabs();

	preventViewportJumpingOnAfterPostPaneSwaps();
};

/**
 * Handle the user clicking the (blog) "subscribe" button on the simplified, email-address-only 
 * subscribe form that is placed on each post.
 */
export const handleClickBlogSubscribe = function(event) {
	//console.log('event = %o', event);

	const subscribeFormEle = event.target.parentNode;
	let invalidEles = null;
	try {
		// This (selecting based on a pseudo-class) will raise an error in IE v9.
		invalidEles = subscribeFormEle.querySelectorAll(':invalid');
	} catch (error) {
        console.error(error);
    }
	
	if (invalidEles && invalidEles.length > 0) {
		//console.log('invalidEles = %o', invalidEles);

		// Allow for the built-in browser form validation to do its thing.
	} else {
		/*
		 * Prevent the context click event from performing the default action, as we are overriding 
		 * that behavior.
		 */
		event.preventDefault();

		const emailInputEle = subscribeFormEle.querySelector('[type="email"]');
		const emailAddress = emailInputEle.value;
		const pageTitle = subscribeFormEle.getAttribute('data-page-title');
		const pageUrl = subscribeFormEle.getAttribute('data-page-url');
		//console.log('emailAddress = %o, pageTitle = %o, pageUrl = %o', emailAddress, pageTitle, pageUrl);

		// Use session storage instead of encoding in the URL.
	    let storage = window.sessionStorage;
	    storage.setItem('subscribeEmailAddress', emailAddress);
	    storage.setItem('subscribePageTitle', pageTitle);
	    storage.setItem('subscribePageUrl', pageUrl);

	    /*
	     * Append a querystring that indicates to the target page that the session storage should 
	     * be interrogated for data.
	     */
	    document.location = '/subscribe?src=post';
	}
};

/**
 * Add click and key event listeners to the widgets that allow for activating/showing each 
 * after-post pane.
 */
const addEventListenersToAfterPostPaneTabs = function() {
	vars.afterPostTabElesList.forEach(tabButtonEle => {
		if (tabButtonEle !== null) {
			//console.log('tabButtonEle.id = %o', tabButtonEle.id);
			tabButtonEle.addEventListener('click', handleClickAfterPostPaneTabs);
			tabButtonEle.addEventListener('keyup', handleKeyupAfterPostPaneTabs);
		}
	});
};

/**
 * Handle the user clicking the widget that activates/shows a corresponding after-post pane. Only 
 * one pane is shown at a time.
 */
const handleClickAfterPostPaneTabs = function(event) {
	//console.log('event = %o', event);
	/*
	 * Prevent the context click event from performing the default action, as we are overriding 
	 * that behavior.
	 */
	event.preventDefault();

	// Establish a clean baseline.
	deactivateTabs();

	// The widget that was clicked.
	const clickedTabButtonEle = event.target;
	activateTab(clickedTabButtonEle);
};

/**
 * Handle the user entering the left/right arrow key while focused on the widget that 
 * activates/shows a corresponding after-post pane. 
 */
const handleKeyupAfterPostPaneTabs = function(event) {
	// The widget that was focused when the key was entered.
	const selectedTabButtonEle = event.target;

	const keyCode = event.keyCode;
	//console.log('keyCode = %o', keyCode);

    const keys = {
    	left: 37,
    	right: 39
    }

    const afterPostTabElesArr = Array.from(vars.afterPostTabElesList);

    // Determine the position of the focused widget within the list of widgets (tabs).
    const tabButtonIdx = afterPostTabElesArr.findIndex(oneTabButtonEle => {
    	return oneTabButtonEle.id === selectedTabButtonEle.id;
    });
    //console.log('tabButtonIdx = %o', tabButtonIdx);

    /*
     * Determine the position of the widget within the list of widgets (tabs) that should end up 
     * being activated, instead.
     */
    let targetTabButtonIdx = null;
    if (keyCode === keys.left) {
    	targetTabButtonIdx = tabButtonIdx - 1;
    	if (targetTabButtonIdx < 0) {
    		targetTabButtonIdx = afterPostTabElesArr.length - 1;
    	}
    } else if (keyCode === keys.right) {
    	targetTabButtonIdx = tabButtonIdx + 1;
    	if (targetTabButtonIdx >= afterPostTabElesArr.length) {
    		targetTabButtonIdx = 0;
    	}
    }

    if (targetTabButtonIdx !== null) {
	    //console.log('targetTabButtonIdx = %o', targetTabButtonIdx);
		const targetTabButtonEle = afterPostTabElesArr[targetTabButtonIdx];
		deactivateTabs();
		activateTab(targetTabButtonEle);
    }
};

/**
 * Activate the given widget (tab button) that activates/shows a corresponding after-post pane,
 * and the corresponding after-post pane, itself.
 */
const activateTab = function(tabButtonEle) {
	/*
	 * When deactivated, the tabindex for all of the the tab button elements are set to -1 to 
	 * remove them as eligible tabbable elements. Upon activation, remove that property value from 
	 * the context tab button and restore the natural tabbability of the button.
	 */
	tabButtonEle.removeAttribute('tabindex');

	tabButtonEle.setAttribute('aria-selected', 'true');
	tabButtonEle.focus();

	// The ID of the pane that should be displayed.
	const controlledTabPanelId = tabButtonEle.getAttribute('aria-controls');
    document.getElementById(controlledTabPanelId).style.display = 'block';

	/*
	 * Handle the edge case where the viewport starts out at the bottom of the document (which 
	 * is possible as the result of refreshing the page) and the client height is reduced prior 
	 * to the user scrolling the viewport.
	 *
	 * window.scrollY is not available in IE v11.
	 */
	adjustMinimumHeightOfBody(window.scrollY || window.pageYOffset);
};

/**
 * Set the after post panes and their corresponding activation widgets to a baseline state.
 */
const deactivateTabs = function() {
	vars.afterPostTabElesList.forEach(tabButtonEle => {
		if (tabButtonEle !== null) {
			// Don't allow any of the widgets (tab buttons) to be tabbable.
			tabButtonEle.setAttribute('tabindex', '-1');
			
			tabButtonEle.setAttribute('aria-selected', 'false');
			//tabButtonEle.removeEventListener('focus', focusEventHandler);
		}
	});

	const tabPanelsArr = document.querySelectorAll('#after-post-box [role="tabpanel"]');
	tabPanelsArr.forEach(tabPanelEle => {
		if (tabPanelEle !== null) {
			tabPanelEle.style.display = 'none';
		}
	});
};

/**
 * Prevent the viewport from jumping up when an after-post pane with a smaller height is swapped 
 * in to replace a currently-displayed after-post pane with a larger height. If the user scrolls 
 * down the page through the vertical real estate claimed by the larger-height pane, then chooses 
 * to swap that larger-height pane out for the smaller-height pane, the client-height of the 
 * document body will truncate, forcing the viewport to jump up the page, which is jarring and 
 * can cause the user to lose their bearings.
 *
 * Explored an implementation based on listening for the "resize" event, but that event is not 
 * fired when the value of document.body.clientHeight changes.
 *
 * Explored an implementation based on the ResizeObserver, but it must be polyfilled to work in 
 * IE v11. More info: 
 *	https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 *	https://github.com/juggle/resize-observer
 *
 * Explored an implementation based on the MutationObserver, but it is not triggered by changes 
 * to the value of document.body.clientHeight.
 *
 * Originally sourced from 
 * https://developer.mozilla.org/en-US/docs/Web/API/Element/scroll_event#scroll_event_throttling
 * More info - https://www.html5rocks.com/en/tutorials/speed/animations/
 */
const preventViewportJumpingOnAfterPostPaneSwaps = function() {
	let ticking = false;

	/*
	 * "Currently, browsers can't know if an event listener will prevent scrolling, so they always 
	 * wait for the listener to finish executing before scrolling the page. Passive event listeners 
	 * solve this problem by letting you indicate that an event listener will never prevent 
	 * scrolling." More info: 
	 *	https://web.dev/uses-passive-event-listeners/
	 */
	const listenerOpts = { passive: true };

	window.addEventListener('scroll', function(event) {
		// window.scrollY is not available in IE v11.
		const currentScrollPosY = window.scrollY || window.pageYOffset;

		// Prevent excessive callbacks.
		if (!ticking) {
			window.requestAnimationFrame(function() {
				adjustMinimumHeightOfBody(currentScrollPosY);
				ticking = false;
			});

			ticking = true;
		}
	}, listenerOpts);
};

/**
 * Set the min-height style of the document body to prevent a) the viewport from jumping up if 
 * the client height is reduced and b) excessive empty space from being left at the bottom of 
 * the page as a result of "a". The browser allows the user to scroll the viewport down to the 
 * larger of document.body.clientHeight or document.body.style.minHeight.
 *
 * Inspired by - https://css-tricks.com/content-jumping-avoid/
 */
const adjustMinimumHeightOfBody = function(currentScrollPosY) {
	//const siteFooterEle = document.querySelector('.site-footer');
	//const siteFooterBottom = siteFooterEle.offsetTop + siteFooterEle.clientHeight;
 	//console.log('siteFooterBottom = %o', siteFooterBottom);
	//const bodyClientHeight = document.body.clientHeight;
	//const bodyMinHeight = document.body.style.minHeight;
	const viewportBottom = currentScrollPosY + window.innerHeight;
 	//console.log('bodyClientHeight = %o, bodyMinHeight = %o, viewportBottom = %o', bodyClientHeight, bodyMinHeight, viewportBottom);

 	/*
 	 * Set the minimum height of the document body to match the bottom of the viewport.
 	 * 
 	 * The purpose here is to establish a minimum height for the body as the user scrolls 
 	 * through the vertical real estate claimed by a currently-displayed larger-height pane. 
 	 * If/when that larger-height pane stops being displayed, having the minimum height set on 
 	 * the body will prevent the viewport from jumping up to the lower client height.
 	 *
 	 * This also serves to reclaim any excessive empty space left at the bottom of the page should 
 	 * the client height end up being less than the body minimum height. This reclamation happens 
 	 * as the user scrolls up through the empty space.
 	 */
	document.body.style.minHeight = viewportBottom + 'px';
	//console.log('document.body.style.minHeight = %s', document.body.style.minHeight);
};

/**
 * If there is a currently-displayed "cancel reply" widget, hide it. This can happen if the 
 * user clicks one "reply" widget, then clicks another "reply" widget.
 */
const cleanUpLeftoverCommentReplyCancelLink = function() {
	const nonBackstageCommentReplyCancelLinkEle = document.querySelector(
		'footer .comment-reply-cancel-link');
	if (nonBackstageCommentReplyCancelLinkEle !== null) {
		const commentReplyLinkEle = vars.backstageMainEle.querySelector('.comment-reply-link');
		util.swapNodes(nonBackstageCommentReplyCancelLinkEle, commentReplyLinkEle);
	}
};

/**
 * Place the comment form in the "ready" state. This may be necessary if the user already 
 * attempted to submit the comment form either in reply to another comment or the parent post. 
 * As a result, the comment form might be in a "success" or "failure" state.
 */
const placeCommentFormInReadyState = function() {
	const commentFormState = vars.commentFormEle.getAttribute(vars.formMutationTriggerAttrName);
	if (commentFormState !== 'ready') {
		vars.commentFormEle.setAttribute(vars.formMutationTriggerAttrName, 'ready');
	}
};

/**
 * Wherever on the page the comment form is, insert it in the DOM right after the given "reply" 
 * widget (which is assumed to be the one that the user clicked).
 */
const moveCommentFormForReply = function(commentReplyLinkEle) {
	// Find the parent/ancestor comment of the given "reply" link.
	const commentBoxEle = commentReplyLinkEle.closest('.comment-box');

	/*
	 * If the user is replying to a top-level comment, indent the comment form. Otherwise, remove 
	 * any previously-placed indenting.
	 */
	if (!commentBoxEle.classList.contains('comment-reply-box')) {
		vars.commentFormBoxEle.classList.add('comment-form-reply-box');	
	} else {
		vars.commentFormBoxEle.classList.remove('comment-form-reply-box');
	}
	commentReplyLinkEle.parentNode.insertBefore(vars.commentFormBoxEle, commentReplyLinkEle.nextSibling);
};

/**
 * Replace the given "reply" widget (which is assumed to be the one that the user clicked) with a 
 * "cancel reply" widget, making sure it is visible. (The "cancel reply" widget might have been 
 * made invisible upon the successful submission of another comment.)
 */
const displayCancelReplyWidget = function(commentReplyLinkEle) {
	const commentReplyCancelLinkEle = vars.backstageMainEle.querySelector('.comment-reply-cancel-link');
	util.swapNodes(commentReplyLinkEle, commentReplyCancelLinkEle);
	commentReplyCancelLinkEle.style.visibility = 'visible';
};

/**
 * Replace the given "cancel reply" widget (which is assumed to be the one that the user clicked) 
 * with a "reply" widget.
 */
const displayReplyWidget = function(commentReplyCancelLinkEle) {
	const commentReplyLinkEle = vars.backstageMainEle.querySelector('.comment-reply-link');
	util.swapNodes(commentReplyCancelLinkEle, commentReplyLinkEle);
};

/**
 * Update the "replyingTo" hidden input element in the comment form to reflect the ID of the 
 * comment associated with the given "reply" widget (which the user is assumed to have clicked).
 */
const identifyParentCommentInCommentForm = function(commentReplyLinkEle) {
	const commentBoxEle = commentReplyLinkEle.closest('.comment-box');
	const commentId = commentBoxEle.getAttribute('data-comment-id');
	const replyingToInputEle = vars.commentFormBoxEle.querySelector('input[name="fields[replyingTo]"]');
	replyingToInputEle.value = commentId;
};

/**
 * Update the "replyingTo" hidden input element in the comment form to reflect the comment as 
 * being in regards to the top-level post (instead of as a reply to an already-existing comment).
 */
const identifyParentAsPostInCommentForm = function() {
	const replyingToInputEle = vars.commentFormBoxEle.querySelector('input[name="fields[replyingTo]"]');
	replyingToInputEle.value = '';
};
